import type { EventLogEntry } from "../types.js";
import { initNavigationSession } from "./navigation.js";

const COOKIE_NAME = 'contextune_session';
const STORAGE_PREFIX = 'ct_events:';
const MAX_AGE_S = 30 * 60;

function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 12);
  }
  return Math.random().toString(36).slice(2, 14);
}

function readCookie(): { id: string; startMs: number } | null {
  try {
    if (typeof document === 'undefined') return null;
    const prefix = `${COOKIE_NAME}=`;
    for (const part of document.cookie.split(';')) {
      const trimmed = part.trim();
      if (!trimmed.startsWith(prefix)) continue;
      const raw = decodeURIComponent(trimmed.slice(prefix.length));
      const sep = raw.indexOf(':');
      if (sep === -1) return null;
      const id = raw.slice(0, sep);
      const startMs = Number(raw.slice(sep + 1));
      if (!id || !Number.isFinite(startMs)) return null;
      return { id, startMs };
    }
    return null;
  } catch {
    return null;
  }
}

function writeCookie(id: string, startMs: number): void {
  try {
    if (typeof document === 'undefined') return;
    const value = encodeURIComponent(`${id}:${startMs}`);
    document.cookie = `${COOKIE_NAME}=${value}; Max-Age=${MAX_AGE_S}; SameSite=Lax; path=/`;
  } catch {
    // sandboxed iframe or restricted context — ignore
  }
}

function expireCookie(): void {
  try {
    if (typeof document === 'undefined') return;
    document.cookie = `${COOKIE_NAME}=; Max-Age=0; SameSite=Lax; path=/`;
  } catch {
    // sandboxed iframe or restricted context — ignore
  }
}

function loadFromStorage(id: string): EventLogEntry[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${id}`);
    if (!raw) return [];
    return JSON.parse(raw) as EventLogEntry[];
  } catch {
    return [];
  }
}

function saveToStorage(id: string, buffer: EventLogEntry[]): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}${id}`, JSON.stringify(buffer));
  } catch {
    // localStorage full or disabled (private mode) — ignore
  }
}

function cleanOldSessions(currentId: string): void {
  try {
    const toDelete: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX) && key !== `${STORAGE_PREFIX}${currentId}`) {
        toDelete.push(key);
      }
    }
    for (const key of toDelete) localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

export function initCtSession() {
  if (typeof window === 'undefined') {
    return initNavigationSession();
  }

  const existing = readCookie();
  const id = existing?.id ?? generateId();
  const startMs = existing?.startMs ?? Date.now();

  writeCookie(id, startMs);
  cleanOldSessions(id);

  return {
    sessionStartMs: startMs,
    loadEvents: (): EventLogEntry[] => loadFromStorage(id),
    save: (buffer: EventLogEntry[]): void => {
      writeCookie(id, startMs);
      saveToStorage(id, buffer);
    },
    // Full purge: remove the persisted event log and the session cookie so the
    // profile cannot be recovered by a subsequent init() (see Contextune.destroy).
    destroy: (): void => {
      try {
        localStorage.removeItem(`${STORAGE_PREFIX}${id}`);
      } catch {
        // localStorage disabled — nothing to remove
      }
      expireCookie();
    },
  };
}
